// Annotation Portal React UI for Manage Students

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTranslation } from "hooks/useTranslation";
import { useTableSelectionModals } from "hooks/useTableSelectionModals";

import EditStudent from "layouts/admin/students/modals/EditStudent";
import DeleteStudent from "layouts/admin/students/modals/DeleteStudent";
import AssignAnnotator from "layouts/admin/students/modals/AssignAnnotator";
import UsersDataTable from "layouts/admin/students/data/UsersDataTable";
import { getAllStudents } from "services/studentService";

function ManageStudents() {
  const { t } = useTranslation();
  const {
    selectedItem: selectedStudent,
    openEdit,
    openDelete,
    handleEdit,
    handleDelete,
    handleCloseDelete,
    handleCloseEdit,
    onPaginationChange,
  } = useTableSelectionModals();
  const [studentsList, setStudentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [assignSlot, setAssignSlot] = useState(null);
  const [assignStudent, setAssignStudent] = useState(null);

  // Fetch ALL students
  const fetchStudentsData = async () => {
    try {
      setLoading(true);
      const res = await getAllStudents();
      setStudentsList(res || []);
    } catch (e) {
      console.error("Failed to load students:", e);
      setStudentsList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = (student, slot) => {
    setAssignStudent(student);
    setAssignSlot(slot); // "annotator1" or "annotator2"
    setOpenAssign(true);
  };

  useEffect(() => {
    fetchStudentsData();
  }, []);

  const { columns, rows } = UsersDataTable({
    users: studentsList,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onAssign: handleAssign, // Make sure this is passed
    t: t, // Pass translation function
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">
                {t("students.title", "Students Management")}
                {loading ? ` (${t("students.loading", "loading...")})` : ""}
              </MDTypography>
              {/* example add btn if you later add AddStudent */}
              {/* <MDButton variant="gradient" color="info">
                <Icon>add</Icon>&nbsp;{t('students.buttons.addStudent', 'Add Student')}
              </MDButton> */}
            </MDBox>

            <MDBox pt={3} pb={3}>
              <Card
                sx={{
                  boxShadow: 4,
                  borderRadius: 2,
                  overflow: "visible",
                  backgroundColor: "#f9fafe",
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDBox>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries
                    canSearch
                    pagination={{ variant: "gradient", color: "info" }}
                    noEndBorder={false}
                    onPaginationChange={onPaginationChange}
                  />
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modals */}
      <EditStudent
        open={openEdit}
        onClose={handleCloseEdit}
        student={selectedStudent}
        onSuccess={fetchStudentsData}
        title={t("students.modals.editStudent", "Edit Student")}
      />
      <DeleteStudent
        key={selectedStudent?.id ?? "closed"}
        open={openDelete}
        onClose={handleCloseDelete}
        student={selectedStudent}
        onSuccess={fetchStudentsData}
      />
    </DashboardLayout>
  );
}

export default ManageStudents;
